/**
 * The Wednesday Drop, as data. Pure: no Supabase, no React, so the server
 * job, the admin builder, the member page and the tests all agree on what a
 * section is and when Wednesday 8am Eastern actually happens.
 *
 * A drop references content; it never copies it. Each item is
 * { section, item_type, item_ref, snapshot }, where item_ref is whatever the
 * content already uses as its id:
 *
 *   drill / xo / insight   drills.id (uuid); snapshot.section says which shelf
 *   masterclass            MASTER_ITEMS moduleKey
 *   concept                CONCEPTS key
 *   research               research_collections.slug
 *   paper                  paper_reads.resource_id (uuid)
 *   plan                   practice_sessions.id (uuid)
 *   set                    drills.id (uuid) of a set of the day; never stored, added from the week's sets
 *
 * The snapshot ({ title, sub, thumb, section }) is only for drawing the card
 * without a second query. Opening an item always goes to the live thing.
 */

// Section headings are Coleman's words from the brief. Rename here and every
// surface (admin, member page, email) follows.
export const SECTIONS = [
  // In the order the drop page walks them (Coleman picked layout C, 2026-10-14).
  { id: "science",  label: "New science module", types: ["concept"], max: 1 },
  { id: "xo",       label: "New X&O breakdowns", types: ["xo"] },
  // The week's sets of the day, added on their own (Coleman, 2026-11-03: "add in recent set of the day videos into
  // the weekly drop starting this week"): every set dated in the 7 days before the drop goes out (weekSetItems).
  { id: "sets",     label: "This week's sets of the day", types: ["set"] },
  { id: "drills",   label: "New drills",         types: ["drill"], target: 15 },
  { id: "research", label: "New research",       types: ["research", "paper"] },
  { id: "masterclass", label: "Masterclass",     types: ["masterclass"] },
  // The one section tied to the week's focus; it only shows when the drop
  // has one (Coleman, 2026-10-15: the focus supplements the drop, it
  // doesn't define it).
  { id: "package",  label: "On this week's focus", types: ["drill", "xo", "insight", "masterclass", "concept", "research", "paper", "plan"] },
];
export const SECTION_BY_ID = Object.fromEntries(SECTIONS.map(s => [s.id, s]));

export const TYPE_LABEL = {
  drill: "Drill", xo: "X&O", insight: "Insight", masterclass: "Masterclass",
  concept: "Science", research: "Research", paper: "Paper", plan: "Practice plan", set: "Set of the day",
};

// A featured concept that isn't new this week is still the science slot, but
// it shouldn't be announced as new.
export function sectionLabel(sectionId, { conceptIsNew = true } = {}) {
  if (sectionId === "science" && !conceptIsNew) return "Science module";
  return SECTION_BY_ID[sectionId]?.label || sectionId;
}

/* "New" only when the drop's concept shipped within a week of the drop, or
   was held back for it. Otherwise the science slot reads "Science module". */
export function scienceIsNew(drop, items = drop?.items) {
  const sci = (items || []).find(i => i.section === "science");
  return !sci?.snapshot?.added || !!sci.snapshot.held
    || (new Date(drop?.publish_at) - new Date(sci.snapshot.added + "T00:00:00")) < 8 * 24 * 3600 * 1000;
}

/* A drop's focus is optional: drops.title, '' when there isn't one. */
export const dropFocus = (drop) => String(drop?.title || "").trim();

/* "1 X&O · 15 drills · 2 research", what's new in a drop, for the cards. */
export function dropCounts(items) {
  const n = (sec) => (items || []).filter(i => i.section === sec).length;
  const parts = [];
  if (n("science")) parts.push("Science module");
  if (n("xo")) parts.push(`${n("xo")} X&O`);
  if (n("sets")) parts.push(`${n("sets")} ${n("sets") === 1 ? "set" : "sets"} of the day`);
  if (n("drills")) parts.push(`${n("drills")} ${n("drills") === 1 ? "drill" : "drills"}`);
  if (n("research")) parts.push(`${n("research")} research`);
  if (n("masterclass")) parts.push(`${n("masterclass")} masterclass${n("masterclass") === 1 ? "" : "es"}`);
  return parts;
}

/* Group a flat item list into the sections, in section order, dropping
   empty sections. Items keep their stored position. With hasFocus false,
   the focus package is left out. */
/* The order a drop walks its sections in. The builder moves sections up and
   down (Coleman, 2026-10-22) and keeps each section's place on its items as
   snapshot.order, so no new column was needed; a section without one keeps
   its default place. */
export function sectionOrder(items) {
  const rank = {};
  for (const it of items || []) {
    const o = it?.snapshot?.order;
    if (Number.isFinite(o)) rank[it.section] = Math.min(rank[it.section] ?? o, o);
  }
  const ids = SECTIONS.map(s => s.id);
  const key = (id) => (rank[id] ?? 100 + ids.indexOf(id));
  return [...ids].sort((a, b) => key(a) - key(b));
}

/* The one item the drop is built around, if the builder starred one (snapshot.featured). */
export const featuredItem = (items) => (items || []).find(i => i?.snapshot?.featured) || null;

export function groupItems(items, { hasFocus = true } = {}) {
  const by = {};
  for (const it of items || []) (by[it.section] = by[it.section] || []).push(it);
  const order = sectionOrder(items);
  return SECTIONS.slice().sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    .filter(s => hasFocus || s.id !== "package")
    .map(s => ({ ...s, items: (by[s.id] || []).slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0)) }))
    .filter(s => s.items.length > 0);
}

// ── Time ────────────────────────────────────────────────────────────────
// Drops are scheduled in Eastern time because that's where the week is run
// from. Stored as timestamptz (UTC); everything shown to an admin is ET.

export const DROP_TZ = "America/New_York";
export const DEFAULT_HOUR = 8;
export const DEFAULT_DOW = 3; // Wednesday

function partsInTz(date, tz = DROP_TZ) {
  const p = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", weekday: "short",
  }).formatToParts(date).reduce((o, x) => { o[x.type] = x.value; return o; }, {});
  const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(p.weekday);
  return { year: +p.year, month: +p.month, day: +p.day, hour: +p.hour % 24, minute: +p.minute, dow };
}

/* "2026-10-21", 8, 0 (Eastern wall clock) -> Date (UTC instant). Works across
   daylight saving because the offset is read for that day, not today. */
export function etToDate(ymd, hour = DEFAULT_HOUR, minute = 0) {
  const [y, m, d] = String(ymd).split("-").map(Number);
  let guess = Date.UTC(y, m - 1, d, hour, minute);
  for (let i = 0; i < 2; i++) {
    const p = partsInTz(new Date(guess));
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    guess += Date.UTC(y, m - 1, d, hour, minute) - asUtc;
  }
  return new Date(guess);
}

/* Date -> { ymd: "2026-10-21", time: "08:00" } in Eastern. */
export function dateToEt(date) {
  const p = partsInTz(new Date(date));
  const pad = (n) => String(n).padStart(2, "0");
  return { ymd: `${p.year}-${pad(p.month)}-${pad(p.day)}`, time: `${pad(p.hour)}:${pad(p.minute)}`, dow: p.dow };
}

/* The next Wednesday 8:00 AM ET at or after `from`. On a Wednesday before
   8am that's today; after 8am it's next week. */
export function nextDropSlot(from = new Date()) {
  const now = partsInTz(from);
  let ahead = (DEFAULT_DOW - now.dow + 7) % 7;
  if (ahead === 0 && (now.hour > DEFAULT_HOUR || (now.hour === DEFAULT_HOUR && now.minute > 0))) ahead = 7;
  const base = new Date(Date.UTC(now.year, now.month - 1, now.day + ahead));
  const ymd = base.toISOString().slice(0, 10);
  return etToDate(ymd, DEFAULT_HOUR, 0);
}

export function fmtEt(date, opts = {}) {
  return new Date(date).toLocaleString("en-US", {
    timeZone: DROP_TZ, weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", ...opts,
  }) + " ET";
}

export function fmtDropDate(date) {
  return new Date(date).toLocaleDateString("en-US", { timeZone: DROP_TZ, month: "long", day: "numeric" });
}

/* Is there a scheduled (or published) drop for the coming Wednesday slot?
   Drives the admin warning. Anything within the slot's ET day counts, so a
   drop moved to 9am still covers that Wednesday. */
export function coversNextSlot(drops, from = new Date()) {
  const slot = dateToEt(nextDropSlot(from)).ymd;
  return (drops || []).some(d => d.status !== "draft" && dateToEt(d.publish_at).ymd === slot);
}

/* The drop that is "this week's" for a member: the newest published one,
   if it went out within the last 7 days. Older than that, Home stops
   featuring it (the archive still has it). */
export function currentDrop(drops, now = new Date()) {
  const live = (drops || [])
    .filter(d => d.status === "published" && new Date(d.publish_at) <= now)
    .sort((a, b) => new Date(b.publish_at) - new Date(a.publish_at));
  const d = live[0];
  if (!d) return null;
  return (now - new Date(d.publish_at)) <= 7 * 24 * 3600 * 1000 ? d : null;
}

// ── Builder helpers ─────────────────────────────────────────────────────

/* The hours a drop can go out at. Drops only leave on the hour (the
   publish job runs hourly), so the builder picks an hour, not a minute. */
export const HOURS = Array.from({ length: 17 }, (_, i) => i + 5).map(h => ({
  value: `${String(h).padStart(2, "0")}:00`,
  label: `${h % 12 || 12}:00 ${h < 12 ? "AM" : "PM"}`,
}));

/* The next whole hour, in Eastern. */
export function nextHourSlot(from = new Date()) {
  const d = new Date(from);
  d.setUTCMinutes(0, 0, 0);
  d.setUTCHours(d.getUTCHours() + 1);
  return d;
}

/* The next n Wednesday slots, for the planning strip. */
export function upcomingSlots(n = 6, from = new Date()) {
  const out = [];
  let slot = nextDropSlot(from);
  for (let i = 0; i < n; i++) {
    out.push(slot);
    slot = nextDropSlot(new Date(slot.getTime() + 60 * 60 * 1000));
  }
  return out;
}

/* What the builder's checklist says. Pure.
   { title, items, drillStatus: {id: status}, copy: {email,push,post,nudge},
     testSent, publishAt }
   -> [{ id, label, state: "ok"|"todo"|"warn"|"info", detail, fix? }] */
export const TARGETS = { xo: 1, drills: 15, research: 2 };

export function readiness({ title, items = [], drillStatus = {}, copy = null, testSent = false, publishAt = null }) {
  const n = (sec) => items.filter(i => i.section === sec).length;
  const out = [];
  out.push({ id: "theme", label: "Focus", state: title && title.trim() ? "ok" : "info",
    detail: title && title.trim() ? title.trim() : "Optional. Without one, the focus section stays hidden" });

  const sci = items.find(i => i.section === "science");
  out.push({ id: "science", label: "Science module", state: sci ? "ok" : "info",
    detail: sci ? (sci.snapshot?.held ? `${sci.snapshot.title} · releases it this week` : sci.snapshot?.title) : "Optional this week" });

  for (const [sec, label] of [["xo", "X&O"], ["drills", "New drills"], ["research", "Research"]]) {
    const have = n(sec), want = TARGETS[sec];
    out.push({ id: sec, label, state: have >= want ? "ok" : have > 0 ? "warn" : "todo", detail: `${have} of ${want}` });
  }

  const drafts = items
    .filter(i => ["drill", "xo", "insight"].includes(i.item_type) && drillStatus[i.item_ref] === "draft")
    .map(i => i.item_ref);
  if (drafts.length) {
    out.push({ id: "drafts", label: "Drafts", state: "warn",
      detail: `${drafts.length} ${drafts.length === 1 ? "drill is" : "drills are"} still a draft and won't go live`,
      fix: { kind: "golive", ids: drafts } });
  }

  if (copy) {
    const missing = Object.entries(copy).filter(([, ok]) => !ok).map(([k]) => k);
    out.push({ id: "copy", label: "Email, push, thread", state: missing.length ? "warn" : "ok",
      detail: missing.length ? `Copy placeholders left: ${missing.join(", ")}` : "Copy ready" });
  }

  out.push({ id: "test", label: "Test send", state: testSent ? "ok" : "todo", detail: testSent ? "Sent to you" : "Send yourself a test" });

  if (publishAt && new Date(publishAt) < new Date()) {
    out.push({ id: "time", label: "Time", state: "warn", detail: "That time has passed: it goes out on the next hourly run" });
  }
  return out;
}


// ── The week's sets of the day (2026-11-03) ─────────────────────────────
export const SETS_DAYS = 7;
const addDays = (ymd, n) => { const d = new Date(`${ymd}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

/** The days whose sets go in a drop: the 7 days before it goes out, Eastern time ({ from, to }, inclusive). */
export function setsWindow(drop) {
  const at = drop?.publish_at || drop?.published_at;
  if (!at || !Number.isFinite(Date.parse(at))) return null;
  const day = dateToEt(at).ymd;
  return { from: addDays(day, -SETS_DAYS), to: addDays(day, -1) };
}

/**
 * The week's sets as drop items, after any X&O (snapshot.order just past the X&O section's), oldest first. `rows` are
 * drills rows: { id, title, mux_playback_id, sotd_date, sotd_kind, coaching_points }. A drop that already has set
 * items keeps its own.
 */
export function weekSetItems(rows, items = []) {
  if ((items || []).some(i => i.section === "sets")) return [];
  const xo = (items || []).filter(i => i.section === "xo").map(i => i?.snapshot?.order).filter(Number.isFinite);
  const order = xo.length ? Math.min(...xo) + 0.5 : undefined;
  return (rows || []).filter(r => r?.id && r.title).slice().sort((a, b) => String(a.sotd_date).localeCompare(String(b.sotd_date)))
    .map((r, i) => {
      const point = Array.isArray(r.coaching_points) ? r.coaching_points[0] : r.coaching_points;
      return { section: "sets", item_type: "set", item_ref: String(r.id), position: i,
        snapshot: { title: r.title, sub: point ? String(point).slice(0, 140) : null, section: "sets", sotdDate: r.sotd_date || null,
          thumb: r.mux_playback_id ? `https://image.mux.com/${r.mux_playback_id}/thumbnail.jpg?width=640&time=2` : null,
          ...(order !== undefined ? { order } : {}) } };
    });
}
